import React, { useState } from "react";
import { Download, FileText, FileJson, FileSpreadsheet, Loader2, X, Printer } from "lucide-react";
import { downloadExport, exportFindingsJSON, type FindingRecord } from "../api/client";

type ExportFormat = "csv" | "json" | "html";
type StatusFilter = "open" | "fixed" | "suppressed" | "all";

type Props = {
  open: boolean;
  onClose: () => void;
  repoId: string;
  repoName: string;
};

export default function ExportReportModal({ open, onClose, repoId, repoName }: Props) {
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [status, setStatus] = useState<StatusFilter>("open");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    try {
      if (format === "csv" || format === "json") {
        await downloadExport(repoId, { format, status });
      } else {
        // HTML Report — fetch JSON then generate report in new tab
        const findings = await exportFindingsJSON(repoId, { status });
        openHtmlReport(findings, repoName, status);
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || "Export failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const FORMAT_OPTIONS: { value: ExportFormat; label: string; desc: string; icon: React.ReactNode }[] = [
    {
      value: "csv",
      label: "CSV",
      desc: "Raw data — import into spreadsheets",
      icon: <FileSpreadsheet className="h-5 w-5" />,
    },
    {
      value: "json",
      label: "JSON",
      desc: "Structured data for integrations",
      icon: <FileJson className="h-5 w-5" />,
    },
    {
      value: "html",
      label: "HTML Report",
      desc: "Styled report — print or save as PDF",
      icon: <FileText className="h-5 w-5" />,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-xl border border-border bg-bg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 bg-surface1">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-text flex items-center gap-2">
                <Download className="h-5 w-5 text-brand" />
                Export Report
              </h2>
              <p className="text-sm text-muted mt-0.5">{repoName}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-muted hover:text-text hover:bg-surface2 transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5 bg-bg">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">Format</label>
            <div className="space-y-2">
              {FORMAT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setFormat(opt.value)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                    format === opt.value
                      ? "border-brand/50 bg-brand/10 ring-1 ring-brand/20"
                      : "border-border bg-surface1 hover:border-border hover:bg-surface2"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${
                      format === opt.value ? "text-brand" : "text-muted"
                    }`}
                  >
                    {opt.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-semibold ${
                        format === opt.value ? "text-text" : "text-text2"
                      }`}
                    >
                      {opt.label}
                    </div>
                    <div className="text-xs text-muted">{opt.desc}</div>
                  </div>
                  {/* Radio indicator */}
                  <div
                    className={`flex-shrink-0 w-4 h-4 rounded-full border-2 transition-all ${
                      format === opt.value
                        ? "border-brand bg-brand"
                        : "border-border"
                    }`}
                  >
                    {format === opt.value && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-text mb-2">
              Finding Status
            </label>
            <div className="flex gap-1 rounded-lg bg-surface2 p-1 border border-border/50">
              {(["open", "fixed", "suppressed", "all"] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setStatus(st)}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 ${
                    status === st
                      ? "bg-surface1 text-text shadow-sm border border-border/50"
                      : "text-muted hover:text-text"
                  }`}
                >
                  {st.charAt(0).toUpperCase() + st.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-critical/10 border border-critical/20">
              <div className="flex items-center gap-2 text-sm text-critical">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex justify-end gap-3 bg-surface1">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-surface1 text-text hover:bg-surface2 transition-colors">
            Cancel
          </button>
          <button onClick={handleExport} disabled={loading} className="px-4 py-2 text-sm font-medium rounded-lg bg-brand text-white hover:bg-brand-hover transition-colors disabled:opacity-50">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </span>
            ) : format === "html" ? (
              <span className="flex items-center gap-2">
                <Printer className="h-4 w-4" />
                Generate Report
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download {format.toUpperCase()}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
   HTML Report Generator — Enterprise Security Assessment Report
   ================================================================ */

/** Severity ranking for sorting (lower = more severe) */
const SEV_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

/** Severity colors */
const SEV_COLORS: Record<string, string> = {
  critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#2563eb", info: "#6b7280",
};

/** Category labels — never exposes raw scanner names */
const KIND_LABELS: Record<string, string> = {
  sast: "Static Analysis (SAST)",
  sca: "Dependency Vulnerabilities (SCA)",
  secret: "Secret Detection",
  iac: "Infrastructure as Code (IaC)",
  license: "License Compliance",
  container: "Container Security",
};

/** Short kind labels for tables */
const KIND_SHORT: Record<string, string> = {
  sast: "SAST", sca: "SCA", secret: "Secret", iac: "IaC", license: "License", container: "Container",
};

type KindKey = "sast" | "sca" | "secret" | "iac" | "license" | "container";

/** Classify a finding into a category based on tool + location_data hints */
function inferKind(tool: string, ld?: Record<string, unknown>): KindKey {
  // The scanner records the kind on every finding; prefer it over tool-name
  // guesses so newer dependency lanes (paxley-vendored-js, paxley-java-archives,
  // paxley-cfml-runtime, paxley-nuget) export as dependency findings.
  const recorded = String((ld?.kind as string) || "").toLowerCase();
  if (["sast", "sca", "secret", "iac", "license", "container"].includes(recorded)) {
    return recorded as KindKey;
  }
  if (!tool) return "sast";
  const t = tool.toLowerCase();
  if (t === "gitleaks") return "secret";
  if (t === "osv-scanner") return "sca";
  if (t.startsWith("paxley-") && t !== "paxley-hygiene") return "sca";
  if (t === "codeql") return "sast";
  if (t === "opengrep") return "sast";
  if (t === "trivy") {
    const locType = ((ld?.type as string) || "").toLowerCase();
    if (locType.includes("license")) return "license";
    if (locType.includes("container") || locType.includes("image")) return "container";
    return "iac";
  }
  if (t === "kics") return "iac";
  if (t === "grype") return "sca";
  return "sast";
}

/** HTML-escape */
function escHtml(str: string): string {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Build the full HTML string and open in a new tab */
function openHtmlReport(
  findings: FindingRecord[],
  repoName: string,
  statusFilter: StatusFilter,
) {
  const esc = escHtml;
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const fullTimestamp = `${dateStr} at ${timeStr}`;
  const statusLabel = statusFilter === "all" ? "All Statuses" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);

  /* ── Statistics ── */
  const bySev: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  const byStatus: Record<string, number> = { open: 0, fixed: 0, suppressed: 0 };
  const byKind: Record<string, FindingRecord[]> = {};
  let withCve = 0;
  let kevCount = 0;
  let withFix = 0;

  for (const f of findings) {
    const sev = (f.severity || "info").toLowerCase();
    bySev[sev] = (bySev[sev] || 0) + 1;
    const st = (f.status || "open").toLowerCase();
    byStatus[st] = (byStatus[st] || 0) + 1;

    const ld = (f.location_data || {}) as Record<string, unknown>;
    const kind = (ld.kind as string) || inferKind(f.tool, ld);
    if (!byKind[kind]) byKind[kind] = [];
    byKind[kind].push(f);

    if (f.cve_id) withCve++;
    if (f.is_kev) kevCount++;
    if (f.recommendation || (ld.fix_guidance as string) || (ld.fix_version as string)) withFix++;
  }

  const total = findings.length;
  const maxSev = Math.max(...Object.values(bySev), 1);

  // Sort all findings globally
  const sorted = [...findings].sort((a, b) => {
    const sd = (SEV_RANK[(a.severity || "info").toLowerCase()] ?? 5) - (SEV_RANK[(b.severity || "info").toLowerCase()] ?? 5);
    if (sd !== 0) return sd;
    return (b.priority_score || 0) - (a.priority_score || 0);
  });

  // Risk score + rating
  const riskScoreNum = bySev.critical * 10 + bySev.high * 5 + bySev.medium * 2 + bySev.low * 0.5;
  const riskRating = riskScoreNum >= 50 ? "Critical" : riskScoreNum >= 20 ? "High" : riskScoreNum >= 5 ? "Medium" : total === 0 ? "Clean" : "Low";
  const riskColor = riskScoreNum >= 50 ? "#dc2626" : riskScoreNum >= 20 ? "#ea580c" : riskScoreNum >= 5 ? "#ca8a04" : "#16a34a";

  // Executive summary paragraph
  const summaryParagraph = total === 0
    ? `The automated security assessment of <strong>${esc(repoName)}</strong> did not identify any findings matching the applied filter (<em>${esc(statusLabel)}</em>). The codebase appears to be in good security standing. Continue to monitor for new vulnerabilities as dependencies and code evolve.`
    : `The automated security assessment of <strong>${esc(repoName)}</strong> identified <strong>${total} finding${total !== 1 ? "s" : ""}</strong> across the scanned codebase. `
      + (bySev.critical > 0 ? `<strong style="color:#dc2626">${bySev.critical} critical-severity</strong> finding${bySev.critical !== 1 ? "s" : ""} require immediate remediation. ` : "")
      + (bySev.high > 0 ? `<strong style="color:#ea580c">${bySev.high} high-severity</strong> finding${bySev.high !== 1 ? "s" : ""} should be prioritized for the next sprint. ` : "")
      + (kevCount > 0 ? `Notably, <strong style="color:#dc2626">${kevCount}</strong> finding${kevCount !== 1 ? "s are" : " is"} linked to CISA Known Exploited Vulnerabilities (KEV), indicating active exploitation in the wild. ` : "")
      + `The overall risk rating is assessed as <strong style="color:${riskColor}">${riskRating}</strong>. `
      + `It is recommended that the development and security teams review the detailed findings below and prioritize remediation based on severity and business impact.`;

  // Ordered list of category keys that have findings
  const categoryOrder: KindKey[] = ["sast", "sca", "secret", "iac", "license", "container"];
  const activeCategories = categoryOrder.filter(k => byKind[k] && byKind[k].length > 0);

  // Section numbering
  const sectionNum = { current: 0 };
  const nextSection = () => { sectionNum.current++; return sectionNum.current; };

  /* ── Helpers for finding cards ── */
  function buildFindingCard(f: FindingRecord, idx: number): string {
    const sev = (f.severity || "info").toLowerCase();
    const ld = (f.location_data || {}) as Record<string, unknown>;
    const desc = (ld.description as string) || "";
    const snippet = (ld.code_snippet as string) || (ld.code_context as string) || "";
    const ruleId = (ld.rule_id as string) || "";
    const fixGuidance = f.recommendation || (ld.fix_guidance as string) || "";
    const fixVersion = (ld.fix_version as string) || "";
    const cweRaw = ld.cwe;
    const cweId = Array.isArray(cweRaw) ? (cweRaw as string[]).join(", ") : (cweRaw as string) || "";
    const pkgName = (ld.package_name as string) || ((ld.package as Record<string, string>)?.name) || "";
    const pkgVersion = ((ld.package as Record<string, string>)?.version) || "";
    const refs = Array.isArray(ld.references) ? (ld.references as string[]).slice(0, 5) : [];
    const kind = (ld.kind as string) || inferKind(f.tool, ld);
    const kindLabel = KIND_LABELS[kind] || kind;
    const statusVal = (f.status || "open").toLowerCase();
    const statusColor = statusVal === "open" ? "#dc2626" : statusVal === "fixed" ? "#16a34a" : "#6b7280";

    // Format code snippet with line numbers if start_line available
    let codeBlockHtml = "";
    if (snippet) {
      const lines = snippet.substring(0, 2000).split("\n");
      const startLine = f.start_line || 1;
      const highlightLine = f.start_line || 0;
      const numberedLines = lines.map((line, li) => {
        const lineNum = startLine + li;
        const isHighlight = lineNum === highlightLine;
        const numStr = String(lineNum).padStart(4, " ");
        const marker = isHighlight ? " &laquo;" : "";
        const bgStyle = isHighlight ? "background:rgba(239,68,68,0.15);display:block;margin:0 -16px;padding:0 16px;" : "";
        return `<span style="${bgStyle}"><span style="color:#64748b;user-select:none;">${numStr} |</span> ${esc(line)}${marker}</span>`;
      });
      codeBlockHtml = `<div class="field"><div class="field-label">Code Context</div><div class="code-block">${numberedLines.join("\n")}</div></div>`;
    }

    // Tags
    const tags: string[] = [];
    if (f.is_kev) tags.push('<span class="tag tag-kev">KEV</span>');
    if (f.is_new) tags.push('<span class="tag tag-new">New</span>');
    if (f.is_reachable) tags.push('<span class="tag tag-reachable">Reachable</span>');
    if (f.fp_likely) tags.push('<span class="tag tag-fp">Likely FP</span>');

    // Scores row
    const scores: string[] = [];
    if (f.priority_score != null) scores.push(`Priority: <strong>${f.priority_score}</strong>/100`);
    if (f.risk_score != null) scores.push(`Risk: <strong>${f.risk_score}</strong>/100`);

    return `
    <div class="finding" id="finding-${idx}">
      <div class="finding-header finding-header-${sev}">
        <div class="finding-num">#${idx}</div>
        <div class="finding-sev"><span class="sev sev-${sev}">${esc(f.severity)}</span></div>
        <div class="finding-main">
          <div class="finding-title">${esc(f.title)}</div>
          ${f.file_path ? `<div class="finding-location">${esc(f.file_path)}${f.start_line ? `:${f.start_line}` : ""}${f.end_line && f.end_line !== f.start_line ? `-${f.end_line}` : ""}</div>` : ""}
        </div>
        <div class="finding-status" style="color:${statusColor}">${statusVal.charAt(0).toUpperCase() + statusVal.slice(1)}</div>
      </div>
      <div class="finding-body">
        <div class="finding-meta-grid">
          ${ruleId ? `<div class="meta-item"><span class="meta-key">Rule</span><span class="meta-val"><code>${esc(ruleId)}</code></span></div>` : ""}
          <div class="meta-item"><span class="meta-key">Category</span><span class="meta-val">${esc(kindLabel)}</span></div>
          <div class="meta-item"><span class="meta-key">Status</span><span class="meta-val" style="color:${statusColor};font-weight:700">${statusVal.charAt(0).toUpperCase() + statusVal.slice(1)}</span></div>
          ${f.first_seen_at ? `<div class="meta-item"><span class="meta-key">First Seen</span><span class="meta-val">${new Date(f.first_seen_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span></div>` : ""}
        </div>

        ${desc ? `<div class="field"><div class="field-label">Description</div><div class="field-value">${esc(desc)}</div></div>` : ""}

        ${codeBlockHtml}

        ${f.cve_id || f.cvss_score || f.epss_score ? `
        <div class="field"><div class="field-label">Vulnerability Intelligence</div><div class="field-value vuln-intel">
          ${f.cve_id ? `<span class="intel-chip"><strong>CVE:</strong> ${esc(f.cve_id)}</span>` : ""}
          ${f.cvss_score ? `<span class="intel-chip"><strong>CVSS:</strong> ${f.cvss_score.toFixed(1)}</span>` : ""}
          ${f.epss_score ? `<span class="intel-chip"><strong>EPSS:</strong> ${(f.epss_score * 100).toFixed(1)}%</span>` : ""}
        </div></div>` : ""}

        ${cweId ? `<div class="field"><div class="field-label">CWE</div><div class="field-value"><code>${esc(cweId)}</code></div></div>` : ""}

        ${pkgName ? `<div class="field"><div class="field-label">Affected Package</div><div class="field-value"><code>${esc(pkgName)}${pkgVersion ? `@${esc(pkgVersion)}` : ""}</code>${fixVersion ? ` &rarr; Fix available: <code style="color:#16a34a;background:#f0fdf4">${esc(fixVersion)}</code>` : ""}</div></div>` : ""}

        ${fixGuidance ? `<div class="field"><div class="field-label">Recommendation</div><div class="field-value recommendation-box">${esc(fixGuidance)}</div></div>` : ""}
        ${!fixGuidance && fixVersion ? `<div class="field"><div class="field-label">Recommendation</div><div class="field-value recommendation-box">Update to version <code>${esc(fixVersion)}</code> or later.</div></div>` : ""}

        ${scores.length > 0 ? `<div class="field"><div class="field-label">Scores</div><div class="field-value scores-row">${scores.join('<span class="score-divider">|</span>')}</div></div>` : ""}

        ${tags.length > 0 ? `<div class="finding-tags">${tags.join("")}</div>` : ""}

        ${refs.length > 0 ? `<div class="field" style="margin-top:12px"><div class="field-label">References</div><div class="field-value">${refs.map(r => `<a href="${esc(r)}" target="_blank" rel="noopener">${esc(r.length > 80 ? r.substring(0, 77) + "..." : r)}</a>`).join("<br>")}</div></div>` : ""}
      </div>
    </div>`;
  }

  /* ── Category summary tables ── */
  function buildCategoryTable(kind: KindKey, items: FindingRecord[], sectionIdx: string): string {
    const kindFindings = [...items].sort((a, b) => {
      const sd = (SEV_RANK[(a.severity || "info").toLowerCase()] ?? 5) - (SEV_RANK[(b.severity || "info").toLowerCase()] ?? 5);
      if (sd !== 0) return sd;
      return (b.priority_score || 0) - (a.priority_score || 0);
    });

    let tableHeader = "";
    let tableRows = "";

    if (kind === "sca") {
      tableHeader = `<tr><th style="width:40px">#</th><th style="width:80px">Severity</th><th>Title</th><th>Package</th><th>Fix Version</th><th>CVE</th><th style="width:80px">Status</th></tr>`;
      tableRows = kindFindings.map((f, i) => {
        const ld = (f.location_data || {}) as Record<string, unknown>;
        const pkg = (ld.package_name as string) || ((ld.package as Record<string, string>)?.name) || "";
        const fix = (ld.fix_version as string) || "";
        const sev = (f.severity || "info").toLowerCase();
        return `<tr>
          <td>${i + 1}</td>
          <td><span class="sev sev-${sev}">${esc(f.severity)}</span></td>
          <td>${esc(f.title)}</td>
          <td class="mono">${esc(pkg)}</td>
          <td>${fix ? `<code style="color:#16a34a">${esc(fix)}</code>` : '<span style="color:#94a3b8">--</span>'}</td>
          <td>${f.cve_id ? `<code>${esc(f.cve_id)}</code>` : '<span style="color:#94a3b8">--</span>'}</td>
          <td>${esc(f.status)}</td>
        </tr>`;
      }).join("");
    } else if (kind === "secret") {
      tableHeader = `<tr><th style="width:40px">#</th><th style="width:80px">Severity</th><th>Title</th><th>File : Line</th><th style="width:80px">Status</th></tr>`;
      tableRows = kindFindings.map((f, i) => {
        const sev = (f.severity || "info").toLowerCase();
        const loc = f.file_path ? `${f.file_path}${f.start_line ? `:${f.start_line}` : ""}` : "";
        return `<tr>
          <td>${i + 1}</td>
          <td><span class="sev sev-${sev}">${esc(f.severity)}</span></td>
          <td>${esc(f.title)}</td>
          <td class="mono">${esc(loc)}</td>
          <td>${esc(f.status)}</td>
        </tr>`;
      }).join("");
    } else if (kind === "license" || kind === "container") {
      tableHeader = `<tr><th style="width:40px">#</th><th style="width:80px">Severity</th><th>Title</th><th>Package</th><th style="width:80px">Status</th></tr>`;
      tableRows = kindFindings.map((f, i) => {
        const ld = (f.location_data || {}) as Record<string, unknown>;
        const pkg = (ld.package_name as string) || ((ld.package as Record<string, string>)?.name) || "";
        const sev = (f.severity || "info").toLowerCase();
        return `<tr>
          <td>${i + 1}</td>
          <td><span class="sev sev-${sev}">${esc(f.severity)}</span></td>
          <td>${esc(f.title)}</td>
          <td class="mono">${esc(pkg)}</td>
          <td>${esc(f.status)}</td>
        </tr>`;
      }).join("");
    } else {
      // sast, iac
      tableHeader = `<tr><th style="width:40px">#</th><th style="width:80px">Severity</th><th>Title</th><th>File : Line</th><th style="width:80px">Status</th></tr>`;
      tableRows = kindFindings.map((f, i) => {
        const sev = (f.severity || "info").toLowerCase();
        const loc = f.file_path ? `${f.file_path}${f.start_line ? `:${f.start_line}` : ""}` : "";
        return `<tr>
          <td>${i + 1}</td>
          <td><span class="sev sev-${sev}">${esc(f.severity)}</span></td>
          <td>${esc(f.title)}</td>
          <td class="mono">${esc(loc)}</td>
          <td>${esc(f.status)}</td>
        </tr>`;
      }).join("");
    }

    return `
      <div class="subsection">
        <h3 id="cat-${kind}">${esc(sectionIdx)} ${esc(KIND_LABELS[kind] || kind)} (${items.length})</h3>
        <table>
          <thead>${tableHeader}</thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>`;
  }

  /* ── Severity x Category matrix ── */
  function buildMatrix(): string {
    const sevs = ["critical", "high", "medium", "low", "info"];
    const cats = categoryOrder;
    const matrix: Record<string, Record<string, number>> = {};
    for (const s of sevs) {
      matrix[s] = {};
      for (const c of cats) matrix[s][c] = 0;
    }
    for (const f of findings) {
      const sev = (f.severity || "info").toLowerCase();
      const ld = (f.location_data || {}) as Record<string, unknown>;
      const kind = (ld.kind as string) || inferKind(f.tool, ld);
      if (matrix[sev] && matrix[sev][kind] !== undefined) {
        matrix[sev][kind]++;
      }
    }
    const activeCats = cats.filter(c => {
      return sevs.some(s => matrix[s][c] > 0);
    });
    if (activeCats.length === 0) return "";
    return `
      <table class="matrix-table">
        <thead><tr><th>Severity</th>${activeCats.map(c => `<th style="text-align:center">${esc(KIND_SHORT[c] || c)}</th>`).join("")}<th style="text-align:center">Total</th></tr></thead>
        <tbody>
          ${sevs.map(s => {
            const rowTotal = activeCats.reduce((sum, c) => sum + matrix[s][c], 0);
            if (rowTotal === 0) return "";
            return `<tr>
              <td><span class="sev sev-${s}">${s}</span></td>
              ${activeCats.map(c => `<td style="text-align:center;font-weight:600">${matrix[s][c] || '<span style="color:#cbd5e1">-</span>'}</td>`).join("")}
              <td style="text-align:center;font-weight:800">${rowTotal}</td>
            </tr>`;
          }).join("")}
          <tr style="border-top:2px solid #e2e8f0">
            <td style="font-weight:800">Total</td>
            ${activeCats.map(c => {
              const colTotal = sevs.reduce((sum, s) => sum + matrix[s][c], 0);
              return `<td style="text-align:center;font-weight:800">${colTotal}</td>`;
            }).join("")}
            <td style="text-align:center;font-weight:800;color:#6366f1">${total}</td>
          </tr>
        </tbody>
      </table>`;
  }

  /* ── Build section numbers ── */
  const coverSec = nextSection();     // 1 - TOC
  const execSec = nextSection();      // 2 - Executive Summary
  const sevDistSec = nextSection();   // 3 - Severity Distribution
  const catSec = activeCategories.length > 0 ? nextSection() : 0; // 4 - Findings by Category
  const detailSec = nextSection();    // 5 - Detailed Findings
  const appendixSec = nextSection();  // 6 - Appendix

  /* ── Build HTML ── */
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Security Assessment Report \u2014 ${esc(repoName)}</title>
<style>
  @page { size: A4; margin: 20mm 18mm; }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    color: #1e293b; background: #ffffff; line-height: 1.65; font-size: 13.5px;
    counter-reset: page-num;
  }

  .page { max-width: 920px; margin: 0 auto; padding: 48px 40px; }

  a { color: #6366f1; text-decoration: none; }
  a:hover { text-decoration: underline; }

  /* ── Cover Page ── */
  .cover-page {
    text-align: center; padding: 80px 40px 60px;
    border-bottom: 4px solid #6366f1; margin-bottom: 48px;
    page-break-after: always;
  }
  .cover-logo { display: inline-flex; align-items: center; gap: 14px; margin-bottom: 40px; }
  .cover-logo svg { width: 48px; height: 48px; }
  .cover-logo span { font-size: 28px; font-weight: 800; color: #6366f1; letter-spacing: -0.03em; }
  .cover-page h1 {
    font-size: 36px; font-weight: 800; color: #0f172a;
    letter-spacing: -0.03em; margin-bottom: 12px; line-height: 1.2;
  }
  .cover-subtitle { font-size: 18px; color: #475569; margin-bottom: 48px; }
  .cover-details {
    display: inline-grid; grid-template-columns: auto auto;
    gap: 8px 32px; text-align: left; font-size: 14px; color: #64748b;
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: 24px 36px;
  }
  .cover-details dt { font-weight: 700; color: #334155; }
  .cover-details dd { margin: 0; }
  .cover-risk-badge {
    display: inline-block; margin-top: 36px; padding: 12px 32px;
    border-radius: 12px; font-size: 16px; font-weight: 800;
    letter-spacing: 0.02em;
  }

  /* ── Table of Contents ── */
  .toc { margin-bottom: 48px; page-break-after: always; }
  .toc h2 { font-size: 20px; font-weight: 800; color: #0f172a; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 3px solid #6366f1; }
  .toc-list { list-style: none; }
  .toc-list li { padding: 10px 0; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
  .toc-list li a { font-size: 14px; font-weight: 600; color: #334155; }
  .toc-list li a:hover { color: #6366f1; }
  .toc-list .toc-sub { padding-left: 24px; }
  .toc-list .toc-sub a { font-weight: 500; color: #64748b; font-size: 13px; }
  .toc-dot { flex: 1; border-bottom: 1px dotted #cbd5e1; margin: 0 12px; }

  /* ── Sections ── */
  .section { margin-bottom: 40px; }
  .section-break { page-break-before: always; }
  .section h2 {
    font-size: 18px; font-weight: 800; color: #0f172a; margin-bottom: 20px;
    padding-bottom: 10px; border-bottom: 3px solid #e2e8f0;
    letter-spacing: -0.01em;
  }
  .section h2 .sec-num { color: #6366f1; margin-right: 8px; }
  .section h3 {
    font-size: 15px; font-weight: 700; color: #334155; margin: 24px 0 12px;
    padding-bottom: 6px; border-bottom: 1px solid #f1f5f9;
  }
  .subsection { margin-bottom: 28px; }
  .section p { font-size: 14px; line-height: 1.75; color: #334155; margin-bottom: 16px; }

  /* ── Metric Grid ── */
  .metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; margin-bottom: 24px; }
  .metric {
    background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
    padding: 18px 12px; text-align: center; transition: box-shadow 0.15s;
  }
  .metric:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .metric .num { font-size: 30px; font-weight: 800; line-height: 1.1; }
  .metric .lbl {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em;
    color: #64748b; margin-top: 6px; font-weight: 600;
  }

  /* ── Risk Banner ── */
  .risk-banner {
    display: flex; align-items: center; justify-content: space-between;
    padding: 24px 32px; border-radius: 12px; margin-bottom: 28px;
  }
  .risk-left h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; font-weight: 700; }
  .risk-left .rating { font-size: 32px; font-weight: 800; line-height: 1.2; }
  .risk-right { text-align: right; font-size: 13px; color: #475569; line-height: 2; }

  /* ── Bar Chart ── */
  .bar-chart { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
  .bar-row { display: flex; align-items: center; gap: 12px; }
  .bar-label { width: 80px; font-size: 12px; font-weight: 700; text-transform: uppercase; text-align: right; letter-spacing: 0.02em; }
  .bar-track { flex: 1; height: 28px; background: #f1f5f9; border-radius: 6px; overflow: hidden; }
  .bar-fill {
    height: 100%; border-radius: 6px; display: flex; align-items: center;
    justify-content: space-between; padding: 0 10px; min-width: 36px;
    transition: width 0.3s ease;
  }
  .bar-fill .bar-count { font-size: 12px; font-weight: 800; color: #fff; }
  .bar-fill .bar-pct { font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.8); }

  /* ── Tables ── */
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin-top: 8px; margin-bottom: 16px; }
  thead th {
    background: #f8fafc; border-bottom: 2px solid #e2e8f0;
    padding: 10px 12px; text-align: left; font-weight: 700;
    color: #475569; font-size: 10.5px; text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  tbody td { padding: 9px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  tbody tr:hover { background: #fafbfd; }
  .mono {
    font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
    font-size: 11.5px; color: #6366f1; word-break: break-all;
  }
  .matrix-table td, .matrix-table th { padding: 8px 14px; }

  /* ── Severity Badge ── */
  .sev {
    display: inline-block; padding: 2px 10px; border-radius: 9999px;
    font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .sev-critical { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .sev-high { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
  .sev-medium { background: #fefce8; color: #a16207; border: 1px solid #fde68a; }
  .sev-low { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
  .sev-info { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }

  /* ── Finding Card ── */
  .finding {
    border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 20px;
    overflow: hidden; break-inside: avoid; box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  }
  .finding-header {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
  }
  .finding-header-critical { border-left: 4px solid #dc2626; }
  .finding-header-high { border-left: 4px solid #ea580c; }
  .finding-header-medium { border-left: 4px solid #ca8a04; }
  .finding-header-low { border-left: 4px solid #2563eb; }
  .finding-header-info { border-left: 4px solid #6b7280; }
  .finding-num { font-size: 12px; font-weight: 800; color: #94a3b8; min-width: 36px; padding-top: 2px; }
  .finding-sev { flex-shrink: 0; padding-top: 1px; }
  .finding-main { flex: 1; min-width: 0; }
  .finding-title { font-size: 14px; font-weight: 700; color: #0f172a; line-height: 1.4; }
  .finding-location {
    font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
    font-size: 12px; color: #6366f1; margin-top: 4px; word-break: break-all;
  }
  .finding-status { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; padding-top: 3px; }
  .finding-body { padding: 18px 20px; font-size: 13px; }

  .finding-meta-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 8px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid #f1f5f9;
  }
  .meta-item { display: flex; flex-direction: column; gap: 2px; }
  .meta-key { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; }
  .meta-val { font-size: 12.5px; color: #334155; }
  .meta-val code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace; font-size: 11px; }

  .field { margin-bottom: 14px; }
  .field-label {
    font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.06em; color: #64748b; margin-bottom: 4px;
  }
  .field-value { color: #334155; line-height: 1.65; font-size: 13px; }
  .field-value code {
    background: #f1f5f9; padding: 1px 6px; border-radius: 4px;
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace; font-size: 11.5px;
  }

  .recommendation-box {
    background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
    padding: 10px 14px; color: #166534; font-size: 12.5px; line-height: 1.6;
  }
  .recommendation-box code { background: #dcfce7; color: #166534; }

  .vuln-intel { display: flex; flex-wrap: wrap; gap: 8px; }
  .intel-chip {
    display: inline-block; background: #f8fafc; border: 1px solid #e2e8f0;
    border-radius: 6px; padding: 4px 10px; font-size: 12px; color: #334155;
  }

  .scores-row { display: flex; gap: 12px; align-items: center; font-size: 13px; }
  .score-divider { color: #cbd5e1; }

  .code-block {
    background: #1e293b; color: #e2e8f0; border-radius: 8px; padding: 14px 16px;
    font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
    font-size: 11.5px; line-height: 1.75; overflow-x: auto; white-space: pre;
    word-break: break-all; margin-top: 6px; border: 1px solid #334155;
  }

  /* Tags */
  .finding-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #f1f5f9; }
  .tag {
    display: inline-block; padding: 3px 10px; border-radius: 9999px;
    font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .tag-kev { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .tag-new { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
  .tag-reachable { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .tag-fp { background: #faf5ff; color: #7c3aed; border: 1px solid #e9d5ff; }

  /* ── Footer ── */
  .report-footer {
    margin-top: 56px; padding-top: 24px; border-top: 3px solid #e2e8f0;
    text-align: center; font-size: 11.5px; color: #94a3b8; line-height: 1.9;
  }
  .report-footer strong { color: #6366f1; }
  .confidential {
    display: inline-block; margin-top: 12px; padding: 4px 16px;
    background: #fef2f2; color: #dc2626; font-size: 10px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.08em; border-radius: 4px;
    border: 1px solid #fecaca;
  }

  /* ── Print Button ── */
  .print-btn {
    position: fixed; bottom: 24px; right: 24px; z-index: 100;
    background: #6366f1; color: #fff; border: none;
    padding: 14px 28px; border-radius: 12px; font-size: 14px; font-weight: 700;
    cursor: pointer; box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    display: flex; align-items: center; gap: 10px; transition: all 0.15s;
  }
  .print-btn:hover { background: #4f46e5; transform: translateY(-1px); box-shadow: 0 6px 24px rgba(99,102,241,0.4); }

  /* ── Print Styles ── */
  @media print {
    .print-btn { display: none !important; }
    body { font-size: 11.5px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 0; max-width: 100%; }
    .cover-page { page-break-after: always; padding: 60px 20px 40px; }
    .toc { page-break-after: always; }
    .section-break { page-break-before: always; }
    .finding { break-inside: avoid; }
    .section { break-inside: avoid-page; }
    .subsection { break-inside: avoid; }
    .bar-fill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .sev { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .metric { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    @bottom-center { content: "Page " counter(page-num); font-size: 10px; color: #94a3b8; }
  }

  /* ── Page Counter (CSS) ── */
  .page-number { display: none; }
  @media print {
    .page-number {
      display: block; position: fixed; bottom: 10mm; right: 18mm;
      font-size: 10px; color: #94a3b8; font-weight: 600;
    }
  }
</style>
</head>
<body>
<div class="page">

  <!-- ==================== COVER PAGE ==================== -->
  <div class="cover-page">
    <div class="cover-logo">
      <svg viewBox="0 0 48 48" fill="none">
        <rect width="48" height="48" rx="12" fill="#6366f1"/>
        <path d="M14 24l7 7 13-13" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>Paxley</span>
    </div>
    <h1>Security Assessment Report</h1>
    <p class="cover-subtitle">${esc(repoName)}</p>
    <dl class="cover-details">
      <dt>Report Date</dt><dd>${esc(fullTimestamp)}</dd>
      <dt>Status Filter</dt><dd>${esc(statusLabel)}</dd>
      <dt>Total Findings</dt><dd>${total}</dd>
      <dt>Categories Scanned</dt><dd>${activeCategories.length > 0 ? activeCategories.map(k => KIND_SHORT[k] || k).join(", ") : "All"}</dd>
    </dl>
    <div class="cover-risk-badge" style="background:${riskColor}12;color:${riskColor};border:2px solid ${riskColor}40">
      Overall Risk: ${riskRating}
    </div>
  </div>

  <!-- ==================== TABLE OF CONTENTS ==================== -->
  <div class="toc" id="toc">
    <h2>Table of Contents</h2>
    <ul class="toc-list">
      <li><a href="#sec-exec">${execSec}. Executive Summary</a><span class="toc-dot"></span></li>
      <li><a href="#sec-sev">${sevDistSec}. Severity Distribution</a><span class="toc-dot"></span></li>
      ${catSec > 0 ? `<li><a href="#sec-cat">${catSec}. Findings by Category</a><span class="toc-dot"></span></li>` : ""}
      ${catSec > 0 ? activeCategories.map((k, i) => `
      <li class="toc-sub"><a href="#cat-${k}">${catSec}.${i + 1} ${KIND_LABELS[k] || k}</a><span class="toc-dot"></span><span style="font-size:12px;color:#94a3b8">${byKind[k].length}</span></li>`).join("") : ""}
      <li><a href="#sec-detail">${detailSec}. Detailed Findings</a><span class="toc-dot"></span><span style="font-size:12px;color:#94a3b8">${total}</span></li>
      <li><a href="#sec-appendix">${appendixSec}. Appendix</a><span class="toc-dot"></span></li>
    </ul>
  </div>

  <!-- ==================== EXECUTIVE SUMMARY ==================== -->
  <div class="section section-break" id="sec-exec">
    <h2><span class="sec-num">${execSec}.</span> Executive Summary</h2>

    <div class="metrics">
      <div class="metric"><div class="num">${total}</div><div class="lbl">Total Findings</div></div>
      <div class="metric"><div class="num" style="color:#dc2626">${bySev.critical}</div><div class="lbl">Critical</div></div>
      <div class="metric"><div class="num" style="color:#ea580c">${bySev.high}</div><div class="lbl">High</div></div>
      <div class="metric"><div class="num" style="color:#ca8a04">${bySev.medium}</div><div class="lbl">Medium</div></div>
      <div class="metric"><div class="num" style="color:#2563eb">${bySev.low}</div><div class="lbl">Low</div></div>
      <div class="metric"><div class="num" style="color:#6b7280">${bySev.info}</div><div class="lbl">Info</div></div>
    </div>

    <!-- Risk Banner -->
    <div class="risk-banner" style="background:${riskColor}0a;border:2px solid ${riskColor}25">
      <div class="risk-left">
        <h3 style="color:${riskColor}">Overall Risk Assessment</h3>
        <div class="rating" style="color:${riskColor}">${riskRating}</div>
      </div>
      <div class="risk-right">
        <div>Open: <strong style="color:#dc2626">${byStatus.open || 0}</strong></div>
        <div>Fixed: <strong style="color:#16a34a">${byStatus.fixed || 0}</strong></div>
        <div>Suppressed: <strong style="color:#6b7280">${byStatus.suppressed || 0}</strong></div>
        ${withCve > 0 ? `<div>With CVE: <strong style="color:#ea580c">${withCve}</strong></div>` : ""}
        ${kevCount > 0 ? `<div>KEV (Exploited): <strong style="color:#dc2626">${kevCount}</strong></div>` : ""}
        ${withFix > 0 ? `<div>Fix Available: <strong style="color:#16a34a">${withFix}</strong></div>` : ""}
      </div>
    </div>

    <p>${summaryParagraph}</p>
  </div>

  <!-- ==================== SEVERITY DISTRIBUTION ==================== -->
  <div class="section" id="sec-sev">
    <h2><span class="sec-num">${sevDistSec}.</span> Severity Distribution</h2>
    <div class="bar-chart">
      ${(["critical", "high", "medium", "low", "info"] as const).filter(s => bySev[s] > 0).map(sev => {
        const count = bySev[sev];
        const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0";
        const barWidth = Math.max(8, (count / maxSev) * 100);
        return `
      <div class="bar-row">
        <div class="bar-label" style="color:${SEV_COLORS[sev]}">${sev}</div>
        <div class="bar-track">
          <div class="bar-fill" style="width:${barWidth}%;background:${SEV_COLORS[sev]}">
            <span class="bar-count">${count}</span>
            <span class="bar-pct">${pct}%</span>
          </div>
        </div>
      </div>`;
      }).join("")}
    </div>
    ${total === 0 ? '<p style="color:#64748b;text-align:center;padding:20px 0">No findings to display.</p>' : ""}
  </div>

  <!-- ==================== FINDINGS BY CATEGORY ==================== -->
  ${catSec > 0 ? `
  <div class="section section-break" id="sec-cat">
    <h2><span class="sec-num">${catSec}.</span> Findings by Category</h2>
    ${activeCategories.map((k, i) => buildCategoryTable(k, byKind[k], `${catSec}.${i + 1}`)).join("")}
  </div>` : ""}

  <!-- ==================== DETAILED FINDINGS ==================== -->
  <div class="section section-break" id="sec-detail">
    <h2><span class="sec-num">${detailSec}.</span> Detailed Findings (${total})</h2>
    ${total === 0
      ? '<p style="color:#64748b;padding:24px 0;text-align:center">No security findings match the selected filter. The codebase appears to be in good security standing.</p>'
      : sorted.map((f, i) => buildFindingCard(f, i + 1)).join("")
    }
  </div>

  <!-- ==================== APPENDIX ==================== -->
  <div class="section section-break" id="sec-appendix">
    <h2><span class="sec-num">${appendixSec}.</span> Appendix</h2>

    <h3>A.1 Scan Metadata</h3>
    <table>
      <tbody>
        <tr><td style="font-weight:700;width:200px">Report Generated</td><td>${esc(fullTimestamp)}</td></tr>
        <tr><td style="font-weight:700">Repository</td><td>${esc(repoName)}</td></tr>
        <tr><td style="font-weight:700">Status Filter</td><td>${esc(statusLabel)}</td></tr>
        <tr><td style="font-weight:700">Total Findings</td><td>${total}</td></tr>
        <tr><td style="font-weight:700">Risk Rating</td><td style="color:${riskColor};font-weight:700">${riskRating}</td></tr>
        <tr><td style="font-weight:700">Risk Score</td><td>${riskScoreNum.toFixed(1)}</td></tr>
      </tbody>
    </table>

    <h3>A.2 Category Distribution</h3>
    <table>
      <thead><tr><th>Category</th><th style="text-align:center">Count</th><th style="text-align:center">Percentage</th></tr></thead>
      <tbody>
        ${categoryOrder.filter(k => byKind[k] && byKind[k].length > 0).map(k => {
          const cnt = byKind[k].length;
          const pct = total > 0 ? ((cnt / total) * 100).toFixed(1) : "0";
          return `<tr><td>${esc(KIND_LABELS[k] || k)}</td><td style="text-align:center;font-weight:700">${cnt}</td><td style="text-align:center">${pct}%</td></tr>`;
        }).join("")}
        ${total > 0 ? `<tr style="border-top:2px solid #e2e8f0"><td style="font-weight:800">Total</td><td style="text-align:center;font-weight:800">${total}</td><td style="text-align:center;font-weight:800">100%</td></tr>` : ""}
      </tbody>
    </table>

    <h3>A.3 Severity x Category Matrix</h3>
    ${buildMatrix() || '<p style="color:#94a3b8">No data to display.</p>'}
  </div>

  <!-- ==================== FOOTER ==================== -->
  <div class="report-footer">
    <strong>Generated by Paxley Security Platform</strong><br>
    ${esc(fullTimestamp)}<br>
    Repository: ${esc(repoName)}<br>
    <div class="confidential">Confidential</div>
  </div>

</div>

<!-- Print / Save as PDF Button -->
<button class="print-btn" onclick="window.print()">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
  Print / Save as PDF
</button>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}
